import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Upload, AlertCircle, Info } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";

interface CountyFusionTextParserProps {
  county: string;
  onImportComplete: () => void;
}

interface ImportResult {
  success: number;
  failed: number;
  skipped: number;
  filteredBusiness: number;
  filteredFamily: number;
  failedRecords: Array<{ line: number; error: string }>;
}

interface ParsedRecord {
  documentNumber: string;
  recordedDate: string | null;
  documentType: string;
  grantorName: string;
  granteeName: string;
  legalDescription: string;
  hasMarginRef: boolean;
}

export function CountyFusionTextParser({ county, onImportComplete }: CountyFusionTextParserProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [pastedText, setPastedText] = useState("");
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // Parse date from MM/DD/YYYY format to YYYY-MM-DD
  const parseDate = (dateStr: string): string | null => {
    if (!dateStr || dateStr.trim() === '') return null;
    
    // Already in YYYY-MM-DD format
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateStr;
    }
    
    // Format: MM/DD/YYYY
    const match = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (match) {
      const month = match[1].padStart(2, '0');
      const day = match[2].padStart(2, '0');
      const year = match[3];
      return `${year}-${month}-${day}`;
    }
    
    return null;
  };

  const getCountyLabel = (countyValue: string): string => {
    const labels: Record<string, string> = {
      'ashland': 'Ashland County, OH',
      'auglaize': 'Auglaize County, OH',
      'belmont': 'Belmont County, OH',
      'butler': 'Butler County, OH',
      'carroll': 'Carroll County, OH',
      'champaign': 'Champaign County, OH',
      'clark': 'Clark County, OH',
      'clermont': 'Clermont County, OH',
      'coshocton': 'Coshocton County, OH',
      'fayette': 'Fayette County, OH',
      'franklin': 'Franklin County, OH',
      'fulton': 'Fulton County, OH',
      'greene': 'Greene County, OH',
      'guernsey': 'Guernsey County, OH',
      'hancock': 'Hancock County, OH',
      'hardin': 'Hardin County, OH',
      'harrison': 'Harrison County, OH',
      'henry': 'Henry County, OH',
      'highland': 'Highland County, OH',
      'jefferson': 'Jefferson County, OH',
      'licking': 'Licking County, OH',
      'logan': 'Logan County, OH',
      'monroe': 'Monroe County, OH',
      'morgan': 'Morgan County, OH',
      'ottawa': 'Ottawa County, OH',
      'pickaway': 'Pickaway County, OH',
      'pike': 'Pike County, OH',
      'preble': 'Preble County, OH',
      'richland': 'Richland County, OH',
      'ross': 'Ross County, OH',
      'sandusky': 'Sandusky County, OH',
      'seneca': 'Seneca County, OH',
      'shelby': 'Shelby County, OH',
      'stark': 'Stark County, OH',
      'tuscarawas': 'Tuscarawas County, OH',
      'vanwert': 'Van Wert County, OH',
      'washington': 'Washington County, OH',
      'wayne': 'Wayne County, OH',
    };
    return labels[countyValue] || `${countyValue.charAt(0).toUpperCase() + countyValue.slice(1)} County`;
  };

  // Detect if text is line-by-line format (from web copy) vs tab-separated
  const isLineSeparatedFormat = (text: string): boolean => {
    const lines = text.trim().split('\n').filter(l => l.trim());
    // Line-separated format has patterns like:
    // Row#, DocNumber, Date, "LAND CONTRACT", "R", Names..., "E", Names..., Legal
    // Check if we see standalone "R" and "E" lines
    const hasStandaloneRE = lines.some(l => l.trim() === 'R' || l.trim() === 'E');
    // Check if document numbers appear on their own lines
    const hasStandaloneDocNums = lines.some(l => l.trim().match(/^20\d{13}$/));
    return hasStandaloneRE && hasStandaloneDocNums;
  };

  // Parse line-by-line format (from web page copy)
  const parseLineSeparatedFormat = (text: string): ParsedRecord[] => {
    const lines = text.trim().split('\n');
    const records: ParsedRecord[] = [];
    
    let i = 0;
    while (i < lines.length) {
      const line = lines[i]?.trim();
      
      // Skip empty lines
      if (!line) {
        i++;
        continue;
      }
      
      // Look for a document number (15-digit number starting with 20)
      const docNumMatch = line.match(/^(20\d{13})$/);
      if (!docNumMatch) {
        // Could be a row number or other content, skip
        i++;
        continue;
      }
      
      const documentNumber = docNumMatch[1];
      i++;
      
      // Find the date (MM/DD/YYYY format)
      let recordedDate: string | null = null;
      while (i < lines.length) {
        const nextLine = lines[i]?.trim();
        if (!nextLine) {
          i++;
          continue;
        }
        const dateMatch = nextLine.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (dateMatch) {
          recordedDate = `${dateMatch[3]}-${dateMatch[1]}-${dateMatch[2]}`;
          i++;
          break;
        }
        // If we hit another doc number, we missed something - backtrack
        if (nextLine.match(/^20\d{13}$/)) break;
        i++;
      }
      
      // Find "LAND CONTRACT"
      let documentType = '';
      while (i < lines.length) {
        const nextLine = lines[i]?.trim();
        if (!nextLine) {
          i++;
          continue;
        }
        if (nextLine.toUpperCase().includes('LAND CONTRACT')) {
          documentType = nextLine;
          i++;
          break;
        }
        // If we hit another doc number, we missed something
        if (nextLine.match(/^20\d{13}$/)) break;
        i++;
      }
      
      if (!documentType) continue;
      
      // Find "R" indicator and collect grantor names
      const grantorNames: string[] = [];
      while (i < lines.length) {
        const nextLine = lines[i]?.trim();
        if (!nextLine) {
          i++;
          continue;
        }
        if (nextLine === 'R') {
          i++;
          // Collect names until we hit "E"
          while (i < lines.length) {
            const nameLine = lines[i]?.trim();
            if (!nameLine) {
              i++;
              continue;
            }
            if (nameLine === 'E') break;
            if (nameLine.match(/^20\d{13}$/)) break;
            // Skip if it looks like a legal description
            if (nameLine.startsWith('City Twp:') || nameLine.startsWith('|Desc')) break;
            grantorNames.push(nameLine);
            i++;
          }
          break;
        }
        if (nextLine.match(/^20\d{13}$/)) break;
        i++;
      }
      
      // Find "E" indicator and collect grantee names
      const granteeNames: string[] = [];
      while (i < lines.length) {
        const nextLine = lines[i]?.trim();
        if (!nextLine) {
          i++;
          continue;
        }
        if (nextLine === 'E') {
          i++;
          // Collect names until we hit legal description or next record
          while (i < lines.length) {
            const nameLine = lines[i]?.trim();
            if (!nameLine) {
              i++;
              continue;
            }
            // Stop at legal description, View, row number, or next doc
            if (nameLine.startsWith('City Twp:') || 
                nameLine.startsWith('|Desc') ||
                nameLine === 'View' ||
                nameLine.match(/^20\d{13}$/) ||
                nameLine.match(/^\d{1,3}\s*$/)) break;
            granteeNames.push(nameLine);
            i++;
          }
          break;
        }
        if (nextLine.match(/^20\d{13}$/)) break;
        if (nextLine.startsWith('City Twp:')) break;
        i++;
      }
      
      // Find legal description
      let legalDescription = '';
      let hasMarginRef = false;
      while (i < lines.length) {
        const nextLine = lines[i]?.trim();
        if (!nextLine) {
          i++;
          continue;
        }
        if (nextLine.startsWith('City Twp:') || nextLine.startsWith('|Desc')) {
          legalDescription = nextLine;
          i++;
          // Check for "View" on next line
          while (i < lines.length) {
            const viewLine = lines[i]?.trim();
            if (!viewLine) {
              i++;
              continue;
            }
            if (viewLine === 'View') {
              hasMarginRef = true;
              i++;
            }
            break;
          }
          break;
        }
        if (nextLine === 'View') {
          hasMarginRef = true;
          i++;
          continue;
        }
        if (nextLine.match(/^20\d{13}$/) || nextLine.match(/^\d{1,3}\s*$/)) break;
        i++;
      }
      
      // Skip records with margin references (releases)
      if (hasMarginRef) continue;
      
      records.push({
        documentNumber,
        recordedDate,
        documentType,
        grantorName: grantorNames.join('; '),
        granteeName: granteeNames.join('; '),
        legalDescription,
        hasMarginRef
      });
    }
    
    console.log(`Parsed ${records.length} land contracts from line-separated format`);
    return records;
  };

  // Parse tab-separated format (traditional CountyFusion export)
  const parseTabSeparatedFormat = (text: string): ParsedRecord[] => {
    const lines = text.trim().split('\n');
    const recordsMap = new Map<string, ParsedRecord>();

    for (const line of lines) {
      if (!line.trim()) continue;

      const fields = line.split('\t');
      
      // Need at least 5 fields for CountyFusion format
      if (fields.length < 5) continue;

      let documentNumber = '';
      let recordedDate = '';
      let documentType = '';
      let name = '';
      let roleIndicator = '';
      let otherName = '';
      let legalDescription = '';
      let marginal = '';

      // Standard CountyFusion format: Instrument#, Book, Page, Recorded, DocType, Name, R/E, Other, Legal, Marginal
      if (fields.length >= 10) {
        documentNumber = fields[0]?.trim() || '';
        recordedDate = fields[3]?.trim() || '';
        documentType = fields[4]?.trim() || '';
        name = fields[5]?.trim() || '';
        roleIndicator = fields[6]?.trim() || '';
        otherName = fields[7]?.trim() || '';
        legalDescription = fields[8]?.trim() || '';
        marginal = fields[9]?.trim() || '';
      } else if (fields.length >= 7) {
        // Compact format without Book/Page
        documentNumber = fields[0]?.trim() || '';
        recordedDate = fields[1]?.trim() || '';
        documentType = fields[2]?.trim() || '';
        name = fields[3]?.trim() || '';
        roleIndicator = fields[4]?.trim() || '';
        otherName = fields[5]?.trim() || '';
        legalDescription = fields[6]?.trim() || '';
        marginal = fields.length > 7 ? fields[7]?.trim() || '' : '';
      }

      // Skip header rows or invalid data
      if (!documentNumber || documentNumber.toLowerCase() === 'instrument' || documentNumber.toLowerCase().includes('instrument #')) continue;
      if (!documentNumber.match(/^\d+$/)) continue;

      // Filter for LAND CONTRACT document types
      const landContractTypes = [
        'LAND CONTRACT', 'CONTRACT FOR DEED', 'INSTALLMENT CONTRACT',
        'LAND INSTALLMENT CONTRACT', 'CONTRACT FOR SALE', 'INSTALLMENT SALE CONTRACT',
        'REAL ESTATE CONTRACT', 'LC'
      ];
      
      const isLandContract = landContractTypes.some(type => 
        documentType.toUpperCase().includes(type) || 
        documentType.toUpperCase() === 'LC'
      );
      
      if (!isLandContract) continue;

      const hasMarginRef = marginal.toLowerCase().includes('view') || 
                           marginal.toLowerCase().includes('release') ||
                           marginal.trim() !== '';

      let grantorName = '';
      let granteeName = '';

      if (roleIndicator.toUpperCase() === 'R' || roleIndicator.toUpperCase() === 'GRANTOR') {
        grantorName = name;
        granteeName = otherName;
      } else if (roleIndicator.toUpperCase() === 'E' || roleIndicator.toUpperCase() === 'GRANTEE') {
        granteeName = name;
        grantorName = otherName;
      } else {
        grantorName = name;
        granteeName = otherName;
      }

      let existingRecord = recordsMap.get(documentNumber);
      if (existingRecord) {
        if (grantorName && !existingRecord.grantorName.includes(grantorName)) {
          existingRecord.grantorName = existingRecord.grantorName 
            ? `${existingRecord.grantorName}; ${grantorName}` 
            : grantorName;
        }
        if (granteeName && !existingRecord.granteeName.includes(granteeName)) {
          existingRecord.granteeName = existingRecord.granteeName 
            ? `${existingRecord.granteeName}; ${granteeName}` 
            : granteeName;
        }
        if (hasMarginRef) {
          existingRecord.hasMarginRef = true;
        }
      } else {
        recordsMap.set(documentNumber, {
          documentNumber,
          recordedDate: parseDate(recordedDate),
          documentType,
          grantorName,
          granteeName,
          legalDescription,
          hasMarginRef
        });
      }
    }

    const records = Array.from(recordsMap.values()).filter(r => !r.hasMarginRef);
    console.log(`Parsed ${records.length} land contracts from tab-separated format`);
    return records;
  };

  // Check if a name looks like a business entity
  const isBusinessEntity = (name: string): boolean => {
    if (!name) return false;
    const upperName = name.toUpperCase();
    const businessIndicators = [
      'LLC', 'L.L.C.', 'INC', 'INC.', 'CORP', 'CORP.', 'CORPORATION',
      'LTD', 'LTD.', 'LIMITED', 'LP', 'L.P.', 'LLP', 'L.L.P.',
      'COMPANY', 'CO.', 'ENTERPRISES', 'PROPERTIES', 'HOLDINGS',
      'INVESTMENTS', 'REALTY', 'REAL ESTATE', 'MANAGEMENT', 'SERVICES',
      'GROUP', 'PARTNERS', 'PARTNERSHIP', 'TRUST', 'TRUSTEE', 'ESTATE',
      'CAPITAL', 'VENTURES', 'ACQUISITIONS', 'DEVELOPMENT', 'BUILDERS',
      'CONSTRUCTION', 'RENTALS', 'LEASING', 'BANK', 'CUSTODIAN',
      'CHURCH', 'MINISTRIES', 'FOUNDATION'
    ];
    return businessIndicators.some(indicator => upperName.includes(indicator));
  };

  // Extract last names from a name string
  const extractLastNames = (nameString: string): string[] => {
    if (!nameString) return [];
    
    // Split by semicolons (multiple parties) and process each
    const parties = nameString.split(';').map(s => s.trim()).filter(s => s);
    const lastNames: string[] = [];
    
    for (const party of parties) {
      // Skip if it's a business
      if (isBusinessEntity(party)) continue;
      
      // Remove common suffixes like JR, SR, II, III, etc.
      let cleaned = party
        .replace(/\b(JR|SR|II|III|IV|V|AKA|NKA|FKA|TRUSTEE|MEMBER|MANAGER)\b/gi, '')
        .trim();
      
      // Split by spaces and get the first word (typically last name in "LASTNAME FIRSTNAME" format)
      const words = cleaned.split(/\s+/).filter(w => w);
      if (words.length > 0) {
        lastNames.push(words[0].toUpperCase());
      }
    }
    
    return [...new Set(lastNames)]; // Remove duplicates
  };

  // Check if buyer and seller share a last name (family transfer)
  const isFamilyTransfer = (grantorName: string, granteeName: string): boolean => {
    const grantorLastNames = extractLastNames(grantorName);
    const granteeLastNames = extractLastNames(granteeName);
    
    // If either party is only businesses, not a family transfer
    if (grantorLastNames.length === 0 || granteeLastNames.length === 0) return false;
    
    // Check for any matching last names
    return grantorLastNames.some(lastName => granteeLastNames.includes(lastName));
  };

  // Filter records to remove businesses and family transfers
  const filterRecords = (records: ParsedRecord[]): { 
    filtered: ParsedRecord[]; 
    businessCount: number; 
    familyCount: number; 
  } => {
    let businessCount = 0;
    let familyCount = 0;
    
    const filtered = records.filter(record => {
      // Check if grantor (seller) is a business
      if (isBusinessEntity(record.grantorName)) {
        console.log(`Filtered business seller: ${record.grantorName}`);
        businessCount++;
        return false;
      }
      
      // Check for family transfer
      if (isFamilyTransfer(record.grantorName, record.granteeName)) {
        console.log(`Filtered family transfer: ${record.grantorName} -> ${record.granteeName}`);
        familyCount++;
        return false;
      }
      
      return true;
    });
    
    return { filtered, businessCount, familyCount };
  };

  const parseCountyFusionText = (text: string): ParsedRecord[] => {
    // Auto-detect format and use appropriate parser
    let records: ParsedRecord[];
    if (isLineSeparatedFormat(text)) {
      console.log('Detected line-separated format (web copy)');
      records = parseLineSeparatedFormat(text);
    } else {
      console.log('Using tab-separated format parser');
      records = parseTabSeparatedFormat(text);
    }
    return records;
  };

  const processText = async (text: string) => {
    setIsProcessing(true);
    setProgress(0);
    setImportResult(null);

    try {
      const rawRecords = parseCountyFusionText(text);
      
      if (rawRecords.length === 0) {
        toast.error("No valid land contract records found. Make sure you're copying data with 'LAND CONTRACT' as the document type.");
        setIsProcessing(false);
        return;
      }

      // Filter out businesses and family transfers
      const { filtered: records, businessCount, familyCount } = filterRecords(rawRecords);
      
      console.log(`After filtering: ${records.length} records (${businessCount} business sellers, ${familyCount} family transfers removed)`);
      
      if (records.length === 0) {
        toast.info(`All ${rawRecords.length} records were filtered out (${businessCount} business sellers, ${familyCount} family transfers)`);
        setImportResult({
          success: 0,
          failed: 0,
          skipped: 0,
          filteredBusiness: businessCount,
          filteredFamily: familyCount,
          failedRecords: []
        });
        setIsProcessing(false);
        return;
      }

      console.log(`Attempting to insert ${records.length} records for ${county}`);
      
      const result: ImportResult = {
        success: 0,
        failed: 0,
        skipped: 0,
        filteredBusiness: businessCount,
        filteredFamily: familyCount,
        failedRecords: []
      };

      for (let i = 0; i < records.length; i++) {
        const record = records[i];
        setProgress(Math.round(((i + 1) / records.length) * 100));

        try {
          // Check for duplicate
          const { data: existing } = await supabase
            .from('county_research')
            .select('id, property_address')
            .eq('county', county)
            .eq('document_number', record.documentNumber)
            .maybeSingle();

          if (existing) {
            // If record has an address, skip it (user has manually entered data)
            if (existing.property_address) {
              console.log(`Skipping duplicate with address: ${record.documentNumber}`);
              result.skipped++;
              continue;
            } else {
              // Update existing record to refresh data
              const { error } = await supabase
                .from('county_research')
                .update({
                  grantor_name: record.grantorName || undefined,
                  grantee_name: record.granteeName || undefined,
                  legal_description: record.legalDescription || undefined,
                  updated_at: new Date().toISOString()
                })
                .eq('id', existing.id);

              if (error) {
                console.error('Update error for record', i, ':', error);
                result.failed++;
                result.failedRecords.push({
                  line: i + 1,
                  error: error.message
                });
              } else {
                result.success++;
              }
              continue;
            }
          }

          // Insert new record
          const { error } = await supabase
            .from('county_research')
            .insert({
              county: county,
              document_number: record.documentNumber,
              filing_date: record.recordedDate,
              document_type: record.documentType,
              grantor_name: record.grantorName || null,
              grantee_name: record.granteeName || null,
              legal_description: record.legalDescription || null,
              is_potential_owner_finance: true,
              confidence_score: 100,
              detection_reasons: ['Manually imported land contract'],
              address_needs_review: true,
              status: 'new'
            });

          if (error) {
            console.error('Insert error for record', i, ':', error);
            result.failed++;
            result.failedRecords.push({
              line: i + 1,
              error: error.message
            });
          } else {
            result.success++;
          }
        } catch (err) {
          console.error('Error processing record', i, ':', err);
          result.failed++;
          result.failedRecords.push({
            line: i + 1,
            error: String(err)
          });
        }
      }

      setImportResult(result);
      
      if (result.success > 0) {
        toast.success(`Successfully imported ${result.success} records${result.skipped > 0 ? ` (${result.skipped} duplicates skipped)` : ''}`);
        setPastedText("");
        onImportComplete();
      }
      
      if (result.failed > 0) {
        toast.warning(`${result.failed} records failed to import`);
      }

      if (result.success === 0 && result.skipped > 0) {
        toast.info(`All ${result.skipped} records already exist in the database`);
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(`Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsProcessing(false);
      setProgress(0);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target?.result as string;
      await processText(text);
    };
    reader.readAsText(file);
  };

  const handlePasteImport = () => {
    if (!pastedText.trim()) {
      toast.error("Please paste some text first");
      return;
    }
    processText(pastedText);
  };

  return (
    <div className="space-y-4">
      {importResult && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <div className="space-y-1">
              <div>Import completed: {importResult.success} imported, {importResult.skipped} skipped (duplicates), {importResult.failed} failed</div>
              {(importResult.filteredBusiness > 0 || importResult.filteredFamily > 0) && (
                <div className="text-sm text-muted-foreground">
                  Filtered out: {importResult.filteredBusiness > 0 && `${importResult.filteredBusiness} business sellers`}
                  {importResult.filteredBusiness > 0 && importResult.filteredFamily > 0 && ', '}
                  {importResult.filteredFamily > 0 && `${importResult.filteredFamily} family transfers`}
                </div>
              )}
              {importResult.failedRecords.length > 0 && (
                <div className="mt-2 text-sm">
                  Failed records: {importResult.failedRecords.slice(0, 5).map(r => `Line ${r.line}`).join(', ')}
                  {importResult.failedRecords.length > 5 && ` and ${importResult.failedRecords.length - 5} more`}
                </div>
              )}
            </div>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{getCountyLabel(county)} - CountyFusion Import</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <strong>How to use:</strong>
              <ol className="list-decimal list-inside mt-2 space-y-1 text-sm">
                <li>Go to the CountyFusion website for {getCountyLabel(county)}</li>
                <li>Search for "LAND CONTRACT" as the document type</li>
                <li>Select a date range for new records</li>
                <li>Select all results (Ctrl+A) and copy (Ctrl+C)</li>
                <li>Paste below and click Import</li>
              </ol>
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <label className="text-sm font-medium">Upload Text File</label>
            <div className="flex items-center gap-2">
              <input
                type="file"
                accept=".txt,.csv"
                onChange={handleFileUpload}
                disabled={isProcessing}
                className="block w-full text-sm"
              />
              <Upload className="h-4 w-4" />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Or Paste Data from CountyFusion</label>
            <Textarea
              placeholder={`Paste ${getCountyLabel(county)} land contract data here...

Expected format (tab-separated):
Instrument# | Book | Page | Recorded | Document Type | Name | R/E | Other Name | Legal Description | Marginal`}
              value={pastedText}
              onChange={(e) => setPastedText(e.target.value)}
              disabled={isProcessing}
              rows={12}
              className="font-mono text-xs"
            />
            <Button 
              onClick={handlePasteImport} 
              disabled={isProcessing || !pastedText.trim()}
              className="w-full"
            >
              Import Pasted Data
            </Button>
          </div>

          {isProcessing && (
            <div className="space-y-2">
              <Progress value={progress} />
              <p className="text-sm text-center text-muted-foreground">
                Processing... {progress}%
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <strong>Features:</strong>
          <ul className="list-disc list-inside mt-2 space-y-1">
            <li>Parses CountyFusion/GovOS format used by 38+ Ohio counties</li>
            <li>Automatically filters for LAND CONTRACT document types</li>
            <li>Skips records with release/marginal references</li>
            <li>Detects duplicates by document number</li>
            <li>Groups multiple party rows by document number</li>
            <li>Sets address_needs_review flag for all new records</li>
          </ul>
        </AlertDescription>
      </Alert>
    </div>
  );
}
